import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/api/client';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

const MIN_LENGTH = 10;

/**
 * Lets a signed-in user rotate their own password.
 *
 * The endpoint existed from the start but nothing called it, so the only way to
 * change a password was for an administrator to set one — including for the
 * seeded account, whose credentials are published in the seed script.
 */
export function ChangePasswordDialog({ open, onClose, onChanged }: ChangePasswordDialogProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(null);
    }
  }, [open]);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const ready = current.length > 0 && next.length >= MIN_LENGTH && next === confirm;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      onChanged?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('auth.change_password')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('auth.current_password')}
            type="password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            required
            autoFocus
          />
          <TextField
            label={t('auth.new_password')}
            type="password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            required
            error={tooShort}
            helperText={t('auth.password_rule', { count: MIN_LENGTH })}
          />
          <TextField
            label={t('auth.confirm_password')}
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
            error={mismatch}
            helperText={mismatch ? t('auth.passwords_differ') : undefined}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !ready}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
